import { RoomEvent, Track } from 'livekit-client';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MediaDeviceMenu, useRoomContext } from '@livekit/components-react';
import { TrackToggle } from '@livekit/components-react';
import { ChatIcon, LeaveIcon } from '@livekit/components-react';
import { ChatToggle } from '@livekit/components-react';
import { useLocalParticipantPermissions, usePersistentUserChoices } from '@livekit/components-react';
import { useMaybeLayoutContext } from '@livekit/components-react';
import { supportsScreenSharing } from '@livekit/components-core';
import { toast } from 'react-toastify';
import { StartMediaButton } from './StartMediaButton';
import { mergeProps } from '../utils/mergeProps';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { BackgroundEffects } from './BackgroundEffects';

export function ControlBarLocal({
    variation,
    controls,
    onLeaveIntent,
    saveUserChoices = true,
    ...props
}) {
    const room = useRoomContext();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const layoutContext = useMaybeLayoutContext();
    useEffect(() => {
        if (layoutContext?.widget.state?.showChat !== undefined) {
            setIsChatOpen(layoutContext.widget.state.showChat);
        }
    }, [layoutContext?.widget.state?.showChat]);

    const isTooLittleSpace = useMediaQuery(`(max-width: ${isChatOpen ? 1000 : 760}px)`);
    variation = variation || (isTooLittleSpace ? 'minimal' : 'verbose');

    const visibleControls = { leave: true, ...controls };
    const localPermissions = useLocalParticipantPermissions();

    if (!localPermissions) {
        visibleControls.camera = false;
        visibleControls.chat = false;
        visibleControls.microphone = false;
        visibleControls.screenShare = false;
    } else {
        visibleControls.camera ??= localPermissions.canPublish;
        visibleControls.microphone ??= localPermissions.canPublish;
        visibleControls.screenShare ??= localPermissions.canPublish;
        visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
    }

    const showIcon = useMemo(() => variation === 'minimal' || variation === 'verbose', [variation]);
    const showText = useMemo(() => variation === 'textOnly' || variation === 'verbose', [variation]);

    const browserSupportsScreenSharing = supportsScreenSharing();
    const [isScreenShareEnabled, setIsScreenShareEnabled] = useState(false);
    const [isScreenSharePending, setIsScreenSharePending] = useState(false);

    const htmlProps = mergeProps({ className: 'lk-control-bar' }, props);

    const {
        saveAudioInputEnabled,
        saveVideoInputEnabled,
        saveAudioInputDeviceId,
        saveVideoInputDeviceId,
    } = usePersistentUserChoices({ preventSave: !saveUserChoices });

    const microphoneOnChange = useCallback(
        (enabled, isUserInitiated) => isUserInitiated ? saveAudioInputEnabled(enabled) : null,
        [saveAudioInputEnabled]
    );

    const cameraOnChange = useCallback(
        (enabled, isUserInitiated) => isUserInitiated ? saveVideoInputEnabled(enabled) : null,
        [saveVideoInputEnabled]
    );

    const syncScreenShareState = useCallback(() => {
        const screenPub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        setIsScreenShareEnabled(Boolean(screenPub && !screenPub.isMuted));
    }, [room.localParticipant]);

    useEffect(() => {
        syncScreenShareState();
        room
            .on(RoomEvent.LocalTrackPublished, syncScreenShareState)
            .on(RoomEvent.LocalTrackUnpublished, syncScreenShareState)
            .on(RoomEvent.TrackMuted, syncScreenShareState)
            .on(RoomEvent.TrackUnmuted, syncScreenShareState);

        return () => {
            room
                .off(RoomEvent.LocalTrackPublished, syncScreenShareState)
                .off(RoomEvent.LocalTrackUnpublished, syncScreenShareState)
                .off(RoomEvent.TrackMuted, syncScreenShareState)
                .off(RoomEvent.TrackUnmuted, syncScreenShareState);
        };
    }, [room, syncScreenShareState]);

    const toggleScreenShare = useCallback(async () => {
        if (isScreenSharePending) return;

        setIsScreenSharePending(true);
        try {
            const nextEnabled = !room.localParticipant.getTrackPublication(Track.Source.ScreenShare);

            await room.localParticipant.setScreenShareEnabled(
                nextEnabled,
                { audio: true },
            );

            syncScreenShareState();
            toast.success(nextEnabled ? 'Screen share started.' : 'Screen share stopped.');
        } catch (err) {
            console.warn('Screen share failed:', err);
            if (err?.name === 'NotAllowedError') {
                toast.error('Screen share denied. Please allow it in your browser.');
            } else if (err?.name === 'NotReadableError') {
                toast.error('Screen share already in use.');
            } else {
                toast.error('Screen share failed.');
            }
        } finally {
            setIsScreenSharePending(false);
        }
    }, [isScreenSharePending, room.localParticipant, syncScreenShareState]);

    const handleLeave = useCallback(() => {
        onLeaveIntent?.();
        room.disconnect();
    }, [onLeaveIntent, room]);

    return (
        <div {...htmlProps}>
            {visibleControls.microphone && (
                <div className="lk-button-group">
                    <TrackToggle
                        source={Track.Source.Microphone}
                        showIcon={showIcon}
                        onChange={microphoneOnChange}
                        onError={(err) => {
                            if (err?.name === 'NotAllowedError') {
                                toast.error('Microphone access denied.');
                            }
                        }}
                    >
                        {showText && 'Microphone'}
                    </TrackToggle>
                    <div className="lk-button-group-menu">
                        <MediaDeviceMenu
                            kind="audioinput"
                            onActiveDeviceChange={(_, deviceId) => saveAudioInputDeviceId(deviceId ?? '')}
                        />
                    </div>
                </div>
            )}

            {visibleControls.camera && (
                <div className="lk-button-group">
                    <TrackToggle
                        source={Track.Source.Camera}
                        showIcon={showIcon}
                        onChange={cameraOnChange}
                        onError={(err) => {
                            if (err?.name === 'NotAllowedError') {
                                toast.error('Camera access denied.');
                            }
                        }}
                    >
                        {showText && 'Camera'}
                    </TrackToggle>
                    <div className="lk-button-group-menu">
                        <MediaDeviceMenu
                            kind="videoinput"
                            onActiveDeviceChange={(_, deviceId) => saveVideoInputDeviceId(deviceId ?? '')}
                        />
                    </div>
                </div>
            )}

            {/* Background Effects Control */}
            {visibleControls.camera && (
                <BackgroundEffects />
            )}

            {visibleControls.screenShare && browserSupportsScreenSharing && (
                <button
                    className="lk-button"
                    disabled={isScreenSharePending}
                    onClick={toggleScreenShare}
                >
                    {showText ? (isScreenShareEnabled ? 'Stop screen share' : 'Share screen') : (isScreenShareEnabled ? '🛑' : '🖥️')}
                </button>
            )}


            {visibleControls.chat && (
                <ChatToggle>
                    {showIcon && <ChatIcon />}
                    {showText && 'Chat'}
                </ChatToggle>
            )}

            {visibleControls.leave && (
                <button className="lk-button" onClick={handleLeave}>
                    {showIcon && <LeaveIcon />}
                    {showText && 'Leave'}
                </button>
            )}

            <StartMediaButton />
        </div>
    );
}
